import type { ReactNode } from "react";
import { Button } from "@ascleo/ui/components/ui/button";
import {
  CircleAlert,
  CreditCard,
  Loader2,
  RotateCw,
  Smartphone,
  UserSearch,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { FEATURE_FLAGS } from "../../../../../platform/defaults";
import { AltCard } from "../../../components/AltCard";
import { CardInsertAnimation } from "../../../components/CardInsertAnimation";
import {
  type CardReaderPhase,
  type CardReaderReadContext,
} from "../../use-card-reader-session";
import { KimAddressQrCode } from "./KimAddressQrCode";
import type { CardCheckKimInfo } from "./use-card-check-controller";

type CardState = CardReaderPhase;
type DetectedCard = CardReaderReadContext["detectedCards"][number];

/**
 * Header title key for the card step, following what the reader is doing
 * right now (KC-31) instead of repeating "Bitte stecken Sie Ihre
 * Versichertenkarte ein." while the card is read, cannot be read or no reader
 * is available (KC-20).
 *
 * @since 1.3.0
 * @example
 * ```ts
 * t(cardCheckTitleKey("reading", false)); // "Ihre Karte wird gelesen …"
 * ```
 */
export function cardCheckTitleKey(
  cardState: CardState,
  showPkvCheckin: boolean,
): string {
  if (showPkvCheckin) return "self-service.check-in.card-check.title-pkv";
  switch (cardState) {
    case "card-detected":
    case "reading":
      return "self-service.check-in.card-check.title-reading";
    case "card-unreadable":
    case "error":
      return "self-service.check-in.card-check.title-unreadable";
    case "no-terminal":
      return "self-service.check-in.card-check.title-no-terminal";
    default:
      return "self-service.check-in.card-check.title";
  }
}

/** Content-sized hero frame shared by every reader state (SPK-04, SPK-05). */
function CardReaderFrame({
  tone,
  children,
}: {
  tone: "primary" | "warning" | "error";
  children: ReactNode;
}) {
  const toneClass =
    tone === "error"
      ? "border-destructive-subtle bg-destructive-soft"
      : tone === "warning"
        ? "border-warning/40 bg-warning-soft"
        : "border-primary-subtle bg-primary-soft";
  return (
    <div
      data-testid="frontdesk-card-check-step"
      className={`flex min-h-72 w-full min-w-0 flex-col items-center justify-center gap-6 rounded-3xl border p-8 text-center text-foreground ${toneClass}`}
    >
      {children}
    </div>
  );
}

function ReaderText({ children }: { children: ReactNode }) {
  return (
    <p className="m-0 max-w-[36ch] text-lg leading-normal text-foreground">
      {children}
    </p>
  );
}

function RetryButton({
  testId,
  onRetry,
}: {
  testId: string;
  onRetry: () => void;
}) {
  const { t } = useTranslation();
  return (
    <Button data-testid={testId} size="2xl" variant="outline" onClick={onRetry}>
      <RotateCw aria-hidden className="size-6" />
      {t("common.retry")}
    </Button>
  );
}

/**
 * Renders the card-reader status, detected card, and retry controls. The
 * header carries the state's title, so the panel shows the illustration and
 * one sentence of guidance, never a second heading (KC-31, KC-32).
 *
 * @since 1.0.0
 * @example
 * ```tsx
 * <CardReaderVisualPanel
 *   cardState="no-card"
 *   detectedCards={[]}
 *   connectionWarning={false}
 *   onRetry={() => undefined}
 * />
 * ```
 */
export function CardReaderVisualPanel({
  cardState,
  errorMessage,
  detectedCards,
  connectionWarning,
  onRetry,
}: {
  cardState: CardState;
  errorMessage?: string;
  detectedCards: DetectedCard[];
  connectionWarning: boolean;
  onRetry: () => void;
}) {
  const { t } = useTranslation();

  switch (cardState) {
    case "loading":
      return (
        <CardReaderFrame tone="primary">
          <Loader2 aria-hidden className="size-16 animate-spin text-primary" />
          <ReaderText>{t("self-service.check-in.card-check.loading")}</ReaderText>
        </CardReaderFrame>
      );

    case "no-terminal":
      return (
        <CardReaderFrame tone="warning">
          <span
            aria-hidden
            className="grid size-16 place-items-center rounded-2xl bg-warning/25 text-warning-ink [&_svg]:size-8"
          >
            <CircleAlert />
          </span>
          <ReaderText>
            {errorMessage ??
              t("self-service.check-in.card-check.no-terminal-body")}
          </ReaderText>
          <RetryButton testId="card-no-terminal-retry-button" onRetry={onRetry} />
        </CardReaderFrame>
      );

    case "card-detected":
    case "reading":
      return (
        <CardReaderFrame tone="primary">
          <Loader2 aria-hidden className="size-16 animate-spin text-primary" />
          {cardState === "card-detected" && detectedCards[0]?.CardHolderName ? (
            <p className="m-0 text-xl font-semibold">
              {detectedCards[0].CardHolderName}
            </p>
          ) : null}
          <ReaderText>{t("self-service.check-in.card-check.reading-detail")}</ReaderText>
        </CardReaderFrame>
      );

    case "card-unreadable":
      return (
        <CardReaderFrame tone="error">
          <CreditCard aria-hidden className="size-16 text-destructive-ink" />
          <ReaderText>{errorMessage}</ReaderText>
          <RetryButton testId="card-unreadable-retry-button" onRetry={onRetry} />
        </CardReaderFrame>
      );

    case "error":
      return (
        <CardReaderFrame tone="error">
          <CreditCard aria-hidden className="size-16 text-destructive-ink" />
          <ReaderText>{errorMessage}</ReaderText>
          <RetryButton testId="retry-button" onRetry={onRetry} />
        </CardReaderFrame>
      );

    case "checking":
    case "no-card":
      return (
        <CardReaderFrame tone="primary">
          <CardInsertAnimation />
          <ReaderText>
            {t("self-service.check-in.card-check.insert-detail")}
          </ReaderText>
          {connectionWarning ? (
            <p className="m-0 flex items-center gap-2 rounded-xl bg-warning-soft px-4 py-2 text-base text-warning-ink">
              <CircleAlert aria-hidden className="size-5 shrink-0" />
              {t("self-service.check-in.card-check.connection-warning")}
            </p>
          ) : null}
        </CardReaderFrame>
      );
  }
}

/**
 * The private-insurance QR path, shown in place of the card illustration so
 * the step does not grow or jump when it opens (SPK-05, KC-82). One wording
 * throughout: "App Ihrer privaten Krankenversicherung".
 *
 * @since 1.3.0
 * @example
 * ```tsx
 * <PkvCheckInPanel kimInfo={{ kimAddress: "praxis@kim.example", practiceName: "Praxis" }} />
 * ```
 */
export function PkvCheckInPanel({
  kimInfo,
}: {
  kimInfo: NonNullable<CardCheckKimInfo>;
}) {
  const { t } = useTranslation();
  return (
    <KimAddressQrCode
      kimAddress={kimInfo.kimAddress}
      practiceName={kimInfo.practiceName}
      size={200}
      instruction={t("self-service.check-in.card-check.pkv-instruction-app")}
    />
  );
}

/**
 * Renders the check-in alternatives next to the card-reader panel: one path
 * without a card and, when configured, the private-insurance app. Both stay
 * equally available while a card is read (KC-29); only the initial settings
 * load disables them.
 *
 * @since 1.0.0
 * @example
 * ```tsx
 * <AlternativeCheckInPanel
 *   cardState="no-card"
 *   kimInfo={null}
 *   showPkvCheckin={false}
 *   onManualFallbackPointerDown={() => undefined}
 *   onNoCard={() => undefined}
 *   onTogglePkvCheckin={() => undefined}
 * />
 * ```
 */
export function AlternativeCheckInPanel({
  cardState,
  kimInfo,
  showPkvCheckin,
  onManualFallbackPointerDown,
  onNoCard,
  onTogglePkvCheckin,
}: {
  cardState: CardState;
  kimInfo: CardCheckKimInfo;
  showPkvCheckin: boolean;
  onManualFallbackPointerDown: () => void;
  onNoCard: () => void;
  onTogglePkvCheckin: () => void;
}) {
  const { t } = useTranslation();
  const disabled = cardState === "loading";

  return (
    <section className="grid content-start gap-3">
      <h2 className="m-0 text-lg font-semibold text-foreground">
        {t("self-service.check-in.card-check.other-ways-title")}
      </h2>

      <AltCard
        icon={<UserSearch />}
        title={t("self-service.check-in.card-check.no-card-title")}
        desc={t("self-service.check-in.card-check.no-card-hint")}
        data-testid="no-card-button"
        className="min-h-20"
        disabled={disabled}
        onPointerDown={onManualFallbackPointerDown}
        onClick={onNoCard}
      />

      {FEATURE_FLAGS.pkvQrCode && kimInfo ? (
        <AltCard
          icon={showPkvCheckin ? <CreditCard /> : <Smartphone />}
          iconTone="primary"
          title={
            showPkvCheckin
              ? t("self-service.check-in.card-check.pkv-close")
              : t("self-service.check-in.card-check.pkv-prompt-title")
          }
          desc={
            showPkvCheckin
              ? undefined
              : t("self-service.check-in.card-check.pkv-prompt-hint")
          }
          data-testid="pkv-checkin-toggle"
          aria-expanded={showPkvCheckin}
          className="min-h-20"
          disabled={disabled}
          onClick={onTogglePkvCheckin}
        />
      ) : null}
    </section>
  );
}
